import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { LayoutDashboard, Boxes, Users, Settings, LogOut, ChevronsUpDown } from "lucide-react";
import type { ReactNode } from "react";
import { license as licenseApi } from "@/lib/api";
import { DravianLogo } from "./logo";
import { SidebarUser } from "./sidebar-user";
import { TopBar } from "./top-bar";
import { FooterStrip } from "./footer-strip";

const NAV: { to: string; label: string; icon: typeof LayoutDashboard; exact?: boolean }[] = [
  { to: "/hq", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/workspaces", label: "Workspaces", icon: Boxes },
  { to: "/team", label: "Team", icon: Users },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function DashboardShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <div className="flex h-screen bg-background">
      <aside className="w-[240px] shrink-0 border-r border-border bg-sidebar flex flex-col">
        <div className="h-[68px] px-5 flex items-center border-b border-border">
          <Link to="/hq" className="shrink-0">
            <DravianLogo />
          </Link>
        </div>
        <div className="p-3">
          <div className="label-eyebrow px-2 mb-2">Organization</div>
          <OrgPill />
        </div>
        <nav className="flex-1 px-2 space-y-0.5">
          {NAV.map((item) => {
            const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] transition ${
                  active
                    ? "bg-primary/15 text-foreground border border-primary/25"
                    : "text-muted-foreground hover:text-foreground hover:bg-sidebar-accent border border-transparent"
                }`}
              >
                <Icon className={`h-4 w-4 ${active ? "text-primary" : ""}`} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border space-y-3">
          <SidebarUser />
          <Link
            to="/"
            className="w-full flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
          >
            <LogOut className="h-3.5 w-3.5" />
            Log out
          </Link>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar hideLogo />
        <main className="flex-1 min-h-0 overflow-auto scrollbar-thin">{children}</main>
        <FooterStrip />
      </div>
    </div>
  );
}

function OrgPill() {
  const licenseQuery = useQuery({
    queryKey: ["license-status"],
    queryFn: () => licenseApi.status(),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
  const orgName = licenseQuery.data?.organization_name ?? "Dravian Horizon";
  const tier = licenseQuery.data?.subscription_tier;
  const state = licenseQuery.data?.state;
  const sub = tier
    ? `${tier.charAt(0).toUpperCase() + tier.slice(1)}${
        state === "grace" ? " · grace" : state === "needs_activation" ? " · needs activation" : ""
      }`
    : "Self-hosted";
  return (
    <button
      className="w-full flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-2.5 text-left hover:bg-surface-elevated transition"
      title={orgName}
    >
      <div className="min-w-0">
        <div className="text-[13px] font-medium truncate">{orgName}</div>
        <div className="text-[10px] text-muted-foreground">{sub}</div>
      </div>
      <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
    </button>
  );
}